import { useState, useMemo, useCallback, useEffect, useContext, useRef } from "react"
import axios from "axios"
import { NavLink, useLocation } from "react-router-dom"
import moment from "moment"
import { sortRows, filterRows } from "../helpers"
import Pagination from "../Pagination"
import { getAdvisorsApi } from "../../Config/config"
import SortIcon from "mdi-react/SortIcon"
import SortAscendingIcon from "mdi-react/SortAscendingIcon"
import SortDescendingIcon from "mdi-react/SortDescendingIcon"
import "../TableView.css"
import { PeFilterContext } from "../../Contexts/PeFilterContext"
import { TableDataContext } from "../../Contexts/TableDataContext"
import { routeNames } from "../../routeSegments"
import NoDataPlaceholder from "../shared/NoDataPlaceholder"
import Loader from "../Loader"
import { UserContext } from "../../Contexts/UserContext"
import Constants from "../../Constants"
import OverflowToolTip from "../shared/OverflowToolTip"
import AddNotePopover from "../shared/AddNotePopover"
import { getSection } from "../../Utils/utils"
import DeleteItemsFromGroup from "../shared/DeleteItemsFromGroup"
import NewLogo from "../NewLogo"
import LZString from "lz-string"

const Table = ({ columns, menu, advisor_type, sub_type_master_id }) => {
  const {
    monthPickerValue,
    setMonthPickerValue,
    isMonthPickerChanged,
    setIsMonthPickerChanged,
    setIsTagClosed,
    refineSearchFilters: contextRefineSearchFilters,
    setRefineSearchFilters: contextSetRefineSearchFilters,
    groupId,
    resetDealsByFilter,
    SubFirmType,
  } = useContext(PeFilterContext)

  const {
    rows,
    setRows,
    data,
    setData,
    isLoad,
    setTableAdvisorId,
    setIsLoad,
    setIsDataLoad,
    currentPage,
    setCurrentPage,
    setCurrentFilter,
    setAllDealIds,
    globalSearchResult,
    setGlobalSearchResult
  } = useContext(TableDataContext)

  const [filters, setFilters] = useState({})
  const [refineSearchFilters, setRefineSearchFilters] = useState(contextRefineSearchFilters || {})
  const [sort, setSort] = useState({ order: "asc", orderBy: "advisor_name_sort" })
  const [isUrlParamsProcessed, setIsUrlParamsProcessed] = useState(false)
  const [urlMonthPickerValue, setUrlMonthPickerValue] = useState(null)
  const { getToken, logout } = useContext(UserContext)
  const abortRef = useRef(null)
  const [reload, setReload] = useState(false)
  const location = useLocation()
  const [urlDates, setUrlDates] = useState(null) // Store URL dates directly

  // Process URL parameters first
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    let hasUpdates = false
    let extractedDates = null


    // Handle 'filters' param
    const compressed = queryParams.get("filters")
    if (compressed) {
      try {
        const decompressed = LZString.decompressFromEncodedURIComponent(compressed)
        const filters = JSON.parse(decompressed)
        setRefineSearchFilters(filters)
        hasUpdates = true
      } catch (error) {
        console.error("Error decompressing or parsing filters:", error)
      }
    }

    // Handle 'gs' param (globalSearchResult)
    const gsCompressed = queryParams.get('gs');
    if (gsCompressed) {
      try {
        const gsDecompressed = LZString.decompressFromEncodedURIComponent(gsCompressed);
        const gsData = JSON.parse(gsDecompressed);
        
        console.log('Retrieved globalSearchResult from URL:', gsData);
        if (gsData && Object.keys(gsData).length > 0) {
          // Set the data in the context
          setGlobalSearchResult(gsData);
          console.log('Set globalSearchResult in context from URL param:', gsData);
          
          // Create a globalSearch entry in refineSearchFilters to trigger the right condition
          const pathKey = location.pathname;
          const updatedFilters = {...refineSearchFilters};
          
          if (!updatedFilters[pathKey]) {
            updatedFilters[pathKey] = {};
          }
          
          updatedFilters[pathKey].globalSearch = ['1']; // Set it to have length 1
          setRefineSearchFilters(updatedFilters);
        }
      } catch (error) {
        console.error('Error decompressing or parsing globalSearchResult:', error);
      }
    }

    // Handle 'date' param - extract and store directly
    const dateParam = queryParams.get("date")
    if (dateParam) {
      console.log("Found date parameter:", dateParam)
      const [startStr, endStr] = dateParam.split(",")
      const startDate = moment(startStr, "YYYY-MM-DD")
      const endDate = moment(endStr, "YYYY-MM-DD")

      if (startDate.isValid() && endDate.isValid()) {
        // Store dates directly for API use
        extractedDates = {
          from_date: startDate.format("YYYY-MM-DD"),
          to_date: endDate.format("YYYY-MM-DD"),
        }
        console.log("Extracted dates from URL:", extractedDates)
        setUrlDates(extractedDates)
      } else {
        console.warn("Invalid date format in query params:", { startStr, endStr })
      }
    }

    // Mark URL params as processed
    setIsUrlParamsProcessed(true)
  }, [location.search, setMonthPickerValue, setRefineSearchFilters])

  useEffect(() => {
    console.log("jjjhhk inv", location?.key)
    resetDealsByFilter()
  }, [location?.key])

  const findActivePage = (page) => {
    setCurrentPage(page)
    tableRender(page, monthPickerValue, sort.orderBy, sort.order, groupId)
    setIsLoad(true)
  }

  const isFromGlobalSearch = () => (refineSearchFilters[location.pathname]?.globalSearch?.length > 0 ? true : false)
  const modifiedPath = location.pathname.endsWith('/all')? location.pathname.substring(0, location.pathname.lastIndexOf('/')): location.pathname;
  const pathKey = modifiedPath?.split("/").slice(0, 3).join("/")
  const globalSearchCategory =
    menu === "pe_vc_advisor" || menu === "vc_advisor"
      ? "investments_ids"
      : menu === "pe_vc_exit" || menu === "vc_exit"
        ? "exits_ids"
        : ""

  const tableRender = useCallback(
    (page, month, column, order, groupId) => {
      let dateRange
      if (urlDates) {
        dateRange = urlDates
        console.log("Using URL dates for API call:", dateRange)
      } else if (monthPickerValue && Array.isArray(monthPickerValue) && monthPickerValue.length === 2) {
        dateRange = {
          from_date: moment(monthPickerValue[0]).format("YYYY-MM-DD"),
          to_date: moment(monthPickerValue[1]).format("YYYY-MM-DD"),
        }
        console.log("Using monthPickerValue for API call:", dateRange)
      } else {
        console.log("No valid dates available for API call, using default or empty.")
        // Provide a default or empty date range if none is found
        dateRange = { from_date: null, to_date: null }
      }

      setIsMonthPickerChanged(0)
      setIsTagClosed(0)

      // Extract PE and VC IDs from SubFirmType
      const peIds = SubFirmType?.filter((firm) => firm.name.includes("(PE)"))?.map((firm) => firm.id)
      const vcIds = SubFirmType?.filter((firm) => firm.name.includes("(VC)"))?.map((firm) => firm.id)

      // Extract IDs from refineSearchFilters
      const refineIds = refineSearchFilters[pathKey]?.firm_Sub_type?.map((firm) => firm.id) || []

      // Filter PE and VC IDs from refineIds
      const filteredPeIds = refineIds.filter((id) => peIds.includes(id))
      const filteredVcIds = refineIds.filter((id) => vcIds.includes(id))

      const category =
        menu == "l-pe" || menu == "l-vc" || menu == "t-pe" || menu == "t-vc" ? "investments" : "investments"
      const dealCategory = menu == "vc_advisor" || menu == "pe_vc_advisor" ? "PEVC" : "EXIT"

      console.log(menu, "menuuuuu")

      const sortBy = dealCategory == "PEVC" ? "advisor_sort" : "advisor_sort"

      axios(`${getAdvisorsApi}`, {
        method: "POST",
        signal: abortRef.current.signal,
        data: {
          category: category,
          type:
            menu == "pe_vc_advisor"
              ? "pe_vc_advisor"
              : menu == "vc_advisor"
                ? "vc_advisor"
                : menu == "pe_vc_ma_advisor"
                  ? "pe_vc_ma_advisor"
                  : menu == "vc_ma_advisor"
                    ? "vc_ma_advisor"
                    : menu == "vc_exit"
                      ? "vc_exit_advisor"
                      : menu == "pe_vc_pm_advisor"
                        ? "pe_vc_pm_advisor"
                        : menu == "pe_vc_exit"
                          ? "pe_exit_advisor"
                          : menu == "vc_pm_advisor"
                            ? "vc_pm_advisor"
                            : "",
          from: isFromGlobalSearch() ? "gs" : "",
          advisor_type: advisor_type,
          date_range:dateRange,
          order_by: {
            column: column,
            direction: order,
          },
          page: page,
          per_page: 200,
          advisor_ids: isFromGlobalSearch()
            ? globalSearchResult?.pe_advisor?.length !== 0
              ? globalSearchResult?.pe_advisor
              : [0]
            : refineSearchFilters[pathKey]?.tadvisors?.map((acq) => acq.id),
          industry: refineSearchFilters[pathKey]?.industry?.map((value) => value.id),
          sector: refineSearchFilters[pathKey]?.sector?.map((value) => value.id),
          stage: refineSearchFilters[modifiedPath]?.stage?.map((value) => value.id),
          round: refineSearchFilters[modifiedPath]?.round?.map((value) => value.id),
          year_founded: refineSearchFilters[modifiedPath]?.yearFounded?.at(0) && {
            from: refineSearchFilters[modifiedPath]?.yearFounded[0].name,
            to: refineSearchFilters[modifiedPath]?.yearFounded[1].name,
          },
          syndication: refineSearchFilters[modifiedPath]?.syndication?.at(0)?.value
            ? [refineSearchFilters[modifiedPath]?.syndication?.at(0)?.value]
            : [],
          deals_involving: refineSearchFilters[modifiedPath]?.dealsInvolving?.map((deal) => deal.value),
          country: refineSearchFilters[modifiedPath]?.countries?.map((country) => country.id),
          investor_type: refineSearchFilters[pathKey]?.investorType?.map((inv) => inv.id),
          region: refineSearchFilters[modifiedPath]?.region?.map((region) => region.id),
          state: refineSearchFilters[modifiedPath]?.state?.map((state) => state.id),
          city: refineSearchFilters[pathKey]?.city?.map((city) => city.id),
          firm_type: {
            firm_type: refineSearchFilters[pathKey]?.firmType?.map((firm) => firm.id),
            pe_firm_sub_type: filteredPeIds.length === peIds.length ? [] : filteredPeIds,
            vc_firm_sub_type: filteredVcIds.length === vcIds.length ? [] : filteredVcIds,
          },
          followOnFundStatus: refineSearchFilters[modifiedPath]?.followexitStatus?.map((status) => status.id),
          exited: refineSearchFilters[modifiedPath]?.exited?.map((status) => status.id),
          deal_investor_range:
            (refineSearchFilters[pathKey]?.dealInvestorRange?.length ?? 0) != 0
              ? {
                  from: refineSearchFilters[pathKey]?.dealInvestorRange?.at(0)?.name,
                  to: refineSearchFilters[pathKey]?.dealInvestorRange?.at(1)?.name,
                }
              : null,
          deals_operator: refineSearchFilters[pathKey]?.deals_operator,
          company_ids: refineSearchFilters[pathKey]?.target_company?.map((company) => company.id),
          investor_ids: refineSearchFilters[pathKey]?.investor_id?.map((acq) => acq.id),
          deal_range: refineSearchFilters[pathKey]?.dealRange?.at(0) && {
            from: refineSearchFilters[pathKey]?.dealRange[0].name,
            to: refineSearchFilters[pathKey]?.dealRange[1].name,
          },
          exit_status: refineSearchFilters[modifiedPath]?.exitStatus?.map((status) => status.id),
          tag:
            refineSearchFilters[pathKey]?.tags?.length != 0
              ? {
                  operator: refineSearchFilters[pathKey]?.operator,
                  data: refineSearchFilters[pathKey]?.tags?.map((tag) => tag.name),
                }
              : null,
          group: {
            group_id: groupId?.myList?.map((id) => id.group_id)?.toString() ?? null,
            sub_type_master_id: groupId?.myList?.length > 0 ? sub_type_master_id : null,
          },
        },
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
        },
      })
        .then((response) => {
          if (response?.data?.message == "Success") {
            setCurrentFilter(response.config.data)
            console.log(response?.data, "axios api response")
            setIsLoad(false)
            setIsDataLoad(false)
            setIsMonthPickerChanged(0)
            setRows([])
            setRows(response?.data?.advisors)
            setData(response?.data)
            setTableAdvisorId([])
            setTableAdvisorId(response?.data?.all_advisor_ids)
            setAllDealIds(response?.data?.all_deal_ids)
          }
        })
        .catch((error) => {
          if (
            error?.response?.status == Constants.sessionTerminatedStatus ||
            error?.response?.status == Constants.InvalidTokenStatus
          ) {
            logout()
          }
          console.log("API not working", error)
        })
    },
    [isMonthPickerChanged, refineSearchFilters],
  )

  // Main effect that triggers API calls - only run after URL params are processed
  useEffect(() => {
    // Don't run if URL params haven't been processed yet
    if (!isUrlParamsProcessed) {
      return
    }

    // Use URL month picker value if available, otherwise use context value
    const effectiveMonthPickerValue = urlMonthPickerValue || monthPickerValue

    // Don't run if month picker value is not ready
    if (
      !effectiveMonthPickerValue ||
      !Array.isArray(effectiveMonthPickerValue) ||
      effectiveMonthPickerValue.length !== 2
    ) {
      console.log("Month picker value not ready for API call:", effectiveMonthPickerValue)
      return
    }

    const abortController = new AbortController()
    abortRef.current = abortController

    setIsLoad(true)
    setIsDataLoad(true)
    setCurrentPage(currentPage)
    tableRender(currentPage, effectiveMonthPickerValue, sort.orderBy, sort.order, groupId)

    return () => {
      abortController.abort()
    }
  }, [tableRender, isUrlParamsProcessed, urlMonthPickerValue, monthPickerValue, refineSearchFilters, groupId, reload])

  const filteredRows = useMemo(() => filterRows(rows, filters), [rows, filters])
  const sortedRows = useMemo(() => sortRows(filteredRows, sort), [filteredRows, sort])
  const calculatedRows = sortedRows
  const count = filteredRows.length

  const handleSort = (accessor) => {
    setRows([])
    setCurrentPage(currentPage)
    setIsDataLoad(true)
    setIsLoad(true)

    const isNumericColumn = ["to_company_investments", "to_company_exits", "to_investors", "to_acquirers"].includes(
      accessor,
    )

    setSort((prevSort) => {
      const isSameColumn = prevSort.orderBy === accessor
      const newOrder = isSameColumn ? (prevSort.order === "desc" ? "asc" : "desc") : isNumericColumn ? "desc" : "asc"

      // Use the current effective month picker value
      const effectiveMonthPickerValue = urlMonthPickerValue || monthPickerValue
      tableRender(currentPage, effectiveMonthPickerValue, accessor, newOrder, groupId)

      return {
        order: newOrder,
        orderBy: accessor,
      }
    })
  }

  const addCommasToNumber = (number) => {
    return number?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  }

  return (
    <>
      <div className="md:col-span-4 col-span-5">
        <div className="lg:col-span-4">
          <div className="scrollbar ">
            <div
              className={
                "overflow-x-auto scrollbar rounded rounded-[6px] rounded-t-none shadow-[0_2px_6px_0_rgba(0,0,0,0.08)] " +
                (rows.length > 5 ? "" : "pr-2")
              }
            >
              <table className="listPage table w-full p-1 bg-grey-50 divide-y">
                <thead className="bg-[#EBEBEB] text-[11pt] leading-[16pt] lg:text-[13px] lg:leading-[15px] font-sans_semi_bold text-[#666666]  text-left">
                  <tr>
                    {columns.map((column, i) => {
                      const sortIcon = () => {
                        if (column.accessor == sort.orderBy) {
                          if (sort.order == "asc") {
                            return <SortDescendingIcon className="pl-2 block h-[15px]" />
                          }
                          return <SortAscendingIcon className="pl-2 block h-[15px]" />
                        } else {
                          return <SortIcon className="pl-2 block h-[15px] " />
                        }
                      }
                      return (
                        <th
                          className={
                            i === 0
                              ? "md:pl-5 md:pr-5 pl-5 pr-[8rem] lg:pr-[13rem] text-left bg-[#EBEBEB] sticky left-0 lg:relative"
                              : "px-5 " + " py-2 font-sans_book_body"
                          }
                          key={i}
                        >
                          <span className="flex whitespace-nowrap">
                            <OverflowToolTip
                              component={
                                <a className="hover:text-[#000000]">
                                  {column.accessor === "add" && groupId?.myList?.length > 0 ? (
                                    "Delete"
                                  ) : column.accessor === "add" ? (
                                    <div className="flex flex-col text-center">
                                      <span>Create</span>
                                      <span>your list</span>
                                    </div>
                                  ) : column.label ? (
                                    column.label
                                  ) : (
                                    "--"
                                  )}
                                </a>
                              }
                            />
                            {column.accessor !== "add" && (
                              <button onClick={() => handleSort(column.accessor, sort.order)}>{sortIcon()}</button>
                            )}
                          </span>
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                {!isLoad ? (
                  count > 0 ? (
                    <tbody className="text-[11pt] leading-[16pt] lg:text-[14px] lg:leading-[16px] font-sans_book_body text-[#333333] text-left">
                      {rows.length > 0 ? (
                        rows?.map((row, i) => {
                          return (
                            <tr
                              key={i}
                              className=" group hover:border-l-4 hover:border-l-[#BC8B20] border-l-4 border-l-[#FFFFFF]   border-b bg-white text"
                            >
                              <td className="md:w-[23%] pl-5 leading-[2rem] text-left sticky left-0 lg:relative text-[#333333] bg-white py-3 font-sans_book_body items-center justify-center">
                                <NewLogo id={row.advisor_id} menu={"advisor"} name={row.advisor_name} />
                                <div className="whitespace-normal break-words">
                                  <OverflowToolTip
                                    className="w-[14rem] md:w-[10rem] lg:w-auto lg:max-w-[13rem] 2xl:max-w-[30rem] flex flex-row gap-2"
                                    component={
                                      <NavLink
                                        className="flex text-start leading-[1.8rem] lg:inline whitespace-normal break-words hover:text-[#BC8B20] font-medium"
                                        state={{ prevPath: location.pathname }}
                                        to={{
                                          pathname:
                                            menu == "vc_advisor" ||
                                            menu == "vc_ma_advisor" ||
                                            menu == "vc_pm_advisor" ||
                                            menu == "vc_exit"
                                              ? routeNames.vcTransactionAdvisorById(row.advisor_id)
                                              : routeNames.peTransactionAdvisorById(row.advisor_id),
                                          search: getSection(
                                            menu == "vc_advisor" || menu == "pe_vc_advisor"
                                              ? Constants.primaryMasterSubIds.pe_vc_advisors_id
                                              : menu == "vc_exit" || menu == "pe_vc_exit"
                                                ? Constants.primaryMasterSubIds.pe_vc_advisors_exits_id
                                                : menu == "vc_ma_advisor" || menu == "pe_vc_ma_advisor"
                                                  ? Constants.primaryMasterSubIds.pe_vc_advisors_ma_id
                                                  : Constants.primaryMasterSubIds.pe_vc_advisors_pm_id,
                                          ),
                                        }}
                                      >
                                        {row.advisor_name ? row.advisor_name : "--"}
                                      </NavLink>
                                    }
                                  />
                                </div>
                              </td>
                              <td className="w-[3%] min-w-[203px]  px-5 py-3 text-center pr-[2.8rem] md:pr-[3rem] font-sans_book_body">
                                <a>
                                  {row.to_company_investments != 0
                                    ? addCommasToNumber(row.to_company_investments)
                                    : "--"}
                                </a>
                              </td>
                              <td className="w-[3%] min-w-[155px] px-5 py-3 text-center pr-[2.8rem] md:pr-[3rem] font-sans_book_body ">
                                {" "}
                                <a>{row.to_company_exits != 0 ? addCommasToNumber(row.to_company_exits) : "--"}</a>
                              </td>
                              <td className="w-[2%] min-w-[120px] px-5 py-3 text-center pr-[2.8rem]  font-sans_book_body">
                                {" "}
                                <a>{row.to_investors != 0 ? addCommasToNumber(row.to_investors) : "--"}</a>
                              </td>
                              <td className="w-[2%] min-w-[120px] px-5 py-3 text-center pr-[2.8rem] md:pr-[3rem] font-sans_book_body">
                                <a>{row.to_acquirers != 0 ? addCommasToNumber(row.to_acquirers) : "--"}</a>
                              </td>
                              <td className="w-[1%] px-[10px] py-3 font-sans_book_body ">
                                {groupId?.myList?.length > 0 ? (
                                  <DeleteItemsFromGroup
                                    id={row.company_id}
                                    group_id={row?.grp_lt_item_id}
                                    type_id={Constants.moduleIds.t_advisor_id}
                                    setReload={setReload}
                                  />
                                ) : (
                                  <AddNotePopover
                                    className="mx-3"
                                    key={row?.advisor_id}
                                    companyId={row?.advisor_id}
                                    companyName={row.advisor_name}
                                    type_id={Constants.moduleIds.t_advisor_id}
                                    sub_type_master_id={sub_type_master_id}
                                  />
                                )}
                              </td>
                            </tr>
                          )
                        })
                      ) : (
                        <center>
                          <div className="h-[4rem] text-[12pt] lg:text-[15px] text-[15px] text-[#333333] relative mt-[3rem] left-[30rem]">
                            No data found
                          </div>
                        </center>
                      )}
                    </tbody>
                  ) : (
                    <></>
                  )
                ) : (
                  <></>
                )}
              </table>
              {!isLoad ? (
                count > 0 ? (
                  <></>
                ) : (
                  <div className="pr-2">
                    <div className="w-full h-[17rem] bg-white flex items-center justify-center fixed md:relative">
                      <NoDataPlaceholder />
                    </div>
                  </div>
                )
              ) : (
                <div className="w-full h-[17rem] bg-white flex items-center justify-center absolute md:relative">
                  <Loader />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Table
